// src/components/resources/tools/generators/ChartMockup/ChartMockupGenerator.tsx

'use client'

import { useCallback, useMemo, useState, useRef } from 'react'
import { clsx } from 'clsx'
import { toast } from 'sonner'
import { BarChart3, Download, LineChart, Palette } from 'lucide-react'
import { sendResourceEvent } from '~/lib/gtag'
import { ExportButton } from '../shared/ExportButton'
import { useExport } from '../shared/useExport'
import { waitForStable } from '../shared/waitForStable'
import { usePreviewDeferral, useStaleRef } from '../shared/usePreviewDeferral'

import { clientLogger, logClientError } from '~/lib/logger-client'

type ChartType = 'bar' | 'line'

type BackgroundPreset = {
  name: string
  value: string
  preview: string
}

interface ChartConfig {
  chartType: ChartType
  title: string
  subtitle: string
  accent: string
  secondary: string
  background: string
  showGrid: boolean
  showLegend: boolean
  dataset: string
}

interface ParsedDataset {
  labels: string[]
  series: { name: string; values: number[] }[]
}

const DEFAULT_DATASET = `Month,Spend,Pipeline\nJan,25000,58000\nFeb,28000,64000\nMar,32000,71000\nApr,36000,82000\nMay,39000,91000`

const BACKGROUND_PRESETS: BackgroundPreset[] = [
  {
    name: 'Dark Gradient',
    value: 'linear-gradient(135deg, #0f172a, #020617)',
    preview: 'from-slate-900 to-slate-950'
  },
  {
    name: 'Blue Ocean',
    value: 'linear-gradient(135deg, #1e3a8a, #0c4a6e)',
    preview: 'from-blue-800 to-sky-800'
  },
  {
    name: 'Purple Night',
    value: 'linear-gradient(135deg, #581c87, #3730a3)',
    preview: 'from-purple-800 to-indigo-700'
  },
  {
    name: 'Forest',
    value: 'linear-gradient(135deg, #14532d, #0f172a)',
    preview: 'from-green-800 to-slate-900'
  },
  {
    name: 'Sunset',
    value: 'linear-gradient(135deg, #dc2626, #ea580c)',
    preview: 'from-red-600 to-orange-600'
  },
  {
    name: 'Clean White',
    value: '#ffffff',
    preview: 'bg-white'
  }
]

const DEFAULT_CONFIG: ChartConfig = {
  chartType: 'bar',
  title: 'Performance lift after creative refresh',
  subtitle: 'Spend vs. pipeline generated',
  accent: '#6366f1',
  secondary: '#22d3ee',
  background: BACKGROUND_PRESETS[0]?.value ?? 'linear-gradient(135deg, #0f172a, #020617)',
  showGrid: true,
  showLegend: true,
  dataset: DEFAULT_DATASET,
}

/**
 * Sanitizes input string to prevent XSS attacks by removing potentially dangerous characters
 * 
 * @param {string} input - The input string to sanitize
 * @returns {string} The sanitized string with dangerous characters removed
 */
function sanitizeInput(input: string): string {
  return input
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, '') // Remove event handlers
    .trim()
}

/**
 * Validates and sanitizes CSS background value to prevent CSS injection attacks
 * Only allows safe CSS patterns like hex colors, rgb/rgba, and basic gradients
 * 
 * @param {string} background - The background CSS value to validate
 * @returns {string} A safe background value or the default if invalid
 */
function sanitizeBackground(background: string): string {
  const sanitized = background.trim()
  
  // Remove any potentially dangerous CSS content
  const cleanedBackground = sanitized
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/expression\s*\(/gi, '') // Remove CSS expressions
    .replace(/url\s*\(/gi, '') // Remove url() functions
    .replace(/@import/gi, '') // Remove @import statements
    .replace(/behavior\s*:/gi, '') // Remove IE behavior property
    .replace(/binding\s*:/gi, '') // Remove XML binding
    .replace(/[<>]/g, '') // Remove HTML tags
  
  // Allow only safe CSS values with strict patterns
  const safePatterns = [
    /^#[0-9a-fA-F]{3,8}$/, // Hex colors (#fff, #ffffff, #ffffffff)
    /^rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)$/, // RGB colors
    /^rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*[0-1]?\.?\d*\s*\)$/, // RGBA colors
    /^linear-gradient\(\s*\d+deg\s*,\s*(#[0-9a-fA-F]{3,8}|rgb\([^)]+\)|rgba\([^)]+\))\s*(,\s*(#[0-9a-fA-F]{3,8}|rgb\([^)]+\)|rgba\([^)]+\))\s*)*\)$/, // Linear gradients with colors only
    /^radial-gradient\(\s*(#[0-9a-fA-F]{3,8}|rgb\([^)]+\)|rgba\([^)]+\))\s*(,\s*(#[0-9a-fA-F]{3,8}|rgb\([^)]+\)|rgba\([^)]+\))\s*)*\)$/, // Radial gradients with colors only
  ]
  
  const isSafe = safePatterns.some(pattern => pattern.test(cleanedBackground))
  return isSafe ? cleanedBackground : DEFAULT_CONFIG.background
}

/**
 * Parses CSV-style dataset into structured data with security limits
 * Limits rows and columns to prevent performance issues and potential DoS attacks
 * 
 * @param {string} dataset - CSV-formatted dataset string
 * @returns {ParsedDataset} Parsed dataset with labels and series data
 */
function parseDataset(dataset: string): ParsedDataset {
  const sanitizedDataset = sanitizeInput(dataset)
  const lines = sanitizedDataset
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .slice(0, 50) // Limit to 50 rows for performance and security

  if (lines.length < 2) {
    return { labels: [], series: [] }
  }

  const headerLine = lines[0]
  if (!headerLine) {
    return { labels: [], series: [] }
  }

  const rows = lines.slice(1)
  const headers = headerLine.split(',').map(label => sanitizeInput(label)).slice(0, 10) // Limit to 10 columns

  const labels: string[] = []
  const series = headers.slice(1).map(name => ({ name, values: [] as number[] }))

  rows.forEach(row => {
    const values = row.split(',')
    if (values.length !== headers.length) return
    
    const firstValue = values[0]
    if (!firstValue) return
    
    const label = sanitizeInput(firstValue)
    labels.push(label)
    
    values.slice(1).forEach((value, index) => {
      const numeric = Number.parseFloat(value.trim())
      // Ensure non-negative values and reasonable bounds to prevent rendering issues
      const safeValue = Number.isFinite(numeric) ? Math.max(0, Math.min(numeric, 1e12)) : 0
      const targetSeries = series[index]
      if (targetSeries) {
        targetSeries.values.push(safeValue)
      }
    })
  })

  return { labels, series }
}

/**
 * Gets the maximum value across all series for chart scaling
 * 
 * @param {ParsedDataset} dataset - The parsed dataset
 * @returns {number} The maximum value found in the dataset
 */
function getMaxValue(dataset: ParsedDataset): number {
  return dataset.series.reduce((max, serie) => {
    const localMax = Math.max(...serie.values, 0)
    return Math.max(max, localMax)
  }, 0)
}

/**
 * Splits a gradient's color-stop list on commas that are not inside
 * `rgb()`/`rgba()` parens, returning the trimmed non-empty stops.
 */
function parseGradientStops(list: string): string[] {
  return list
    .split(/,(?![^(]*\))/)
    .map(s => s.trim())
    .filter(Boolean)
}

/** Applies evenly-spaced color stops to a canvas gradient. */
function applyEvenStops(grad: CanvasGradient, stops: string[]): void {
  stops.forEach((color, index) => {
    grad.addColorStop(stops.length === 1 ? 0 : index / (stops.length - 1), color)
  })
}

/**
 * Paints `background` onto a 2D canvas context so the PNG export matches the
 * on-screen preview. Handles solid colors, `linear-gradient(<angle>deg,
 * <stop>, …)`, and `radial-gradient(<stop>, …)` — the background shapes the
 * presets and `sanitizeBackground` allow (the sanitizer restricts both
 * gradient forms to color stops only). Anything unparseable falls back to
 * dark slate.
 *
 * The linear angle is mapped onto the canvas rectangle the same way CSS
 * defines it (0deg = bottom→top, 90deg = left→right, 135deg = top-left→
 * bottom-right); the radial gradient is centered and sized to the far
 * corner — both so the exported gradient matches the preview.
 */
function paintCanvasBackground(
  ctx: CanvasRenderingContext2D,
  background: string,
  width: number,
  height: number,
): void {
  const FALLBACK = '#020617'
  const value = (background ?? '').trim()

  const linear = /^linear-gradient\(\s*([\d.]+)deg\s*,\s*(.+)\)$/i.exec(value)
  if (linear) {
    const angleDeg = Number.parseFloat(linear[1] ?? '0')
    const stops = parseGradientStops(linear[2] ?? '')

    if (stops.length >= 2) {
      // Convert the CSS angle (0deg points up, increasing clockwise) to a
      // gradient line across the rectangle.
      const rad = (angleDeg % 360) * (Math.PI / 180)
      const dx = Math.sin(rad)
      const dy = -Math.cos(rad)
      const halfW = width / 2
      const halfH = height / 2
      // Project the rectangle's extent onto the gradient line.
      const extent = Math.abs(dx) * halfW + Math.abs(dy) * halfH
      const grad = ctx.createLinearGradient(
        halfW - dx * extent,
        halfH - dy * extent,
        halfW + dx * extent,
        halfH + dy * extent,
      )
      applyEvenStops(grad, stops)
      ctx.fillStyle = grad
      ctx.fillRect(0, 0, width, height)
      return
    }
  }

  const radial = /^radial-gradient\(\s*(.+)\)$/i.exec(value)
  if (radial) {
    const stops = parseGradientStops(radial[1] ?? '')
    if (stops.length >= 2) {
      // sanitizeBackground only permits color-stop radial gradients (no
      // shape/size/position), so reproduce the CSS default: `ellipse
      // farthest-corner at center`. For a center-positioned ellipse the
      // farthest-corner radii are rx = (width/2)·√2 and ry = (height/2)·√2,
      // which keeps the gradient elliptical on the non-square export canvas
      // (a plain circle would mis-place the stops vs. the live preview).
      // Render it by drawing a unit-circle gradient under a (rx, ry) scale.
      const cx = width / 2
      const cy = height / 2
      const rx = cx * Math.SQRT2
      const ry = cy * Math.SQRT2
      const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, 1)
      applyEvenStops(grad, stops)
      ctx.save()
      ctx.translate(cx, cy)
      ctx.scale(rx, ry)
      ctx.fillStyle = grad
      // Fill the whole canvas, expressed in the post-scale coordinate space.
      ctx.fillRect(-cx / rx, -cy / ry, width / rx, height / ry)
      ctx.restore()
      return
    }
  }

  // Solid color (or anything we can't parse → safe dark fallback).
  ctx.fillStyle = value && !value.includes('gradient') ? value : FALLBACK
  ctx.fillRect(0, 0, width, height)
}

/**
 * Bar chart component with responsive SVG rendering
 * 
 * @param {Object} props - Component props
 * @param {ParsedDataset} props.dataset - The chart data
 * @param {ChartConfig} props.config - Chart configuration
 * @returns {JSX.Element} The rendered bar chart SVG
 */
function BarChart({ dataset, config }: { dataset: ParsedDataset; config: ChartConfig }): JSX.Element {
  const maxValue = Math.max(getMaxValue(dataset) * 1.1, 1)
  const barGroupWidth = 60
  const chartHeight = 320
  const chartWidth = dataset.labels.length * barGroupWidth * (dataset.series.length > 1 ? 1.2 : 1)

  return (
    <svg viewBox={`0 0 ${chartWidth + 120} ${chartHeight + 120}`} className="h-full w-full" style={{ fontFamily: 'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif' }}>
      {/* Explicit fontFamily so the labels rasterize identically when the
          SVG is exported to PNG via an <img> (where the page/web font does
          not apply). */}
      <defs>
        <linearGradient id="bar-accent" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={config.accent} stopOpacity={0.95} />
          <stop offset="100%" stopColor={config.accent} stopOpacity={0.3} />
        </linearGradient>
        <linearGradient id="bar-secondary" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={config.secondary} stopOpacity={0.95} />
          <stop offset="100%" stopColor={config.secondary} stopOpacity={0.3} />
        </linearGradient>
      </defs>
      <g transform="translate(80,40)">
        {config.showGrid && (
          <g stroke="rgba(148, 163, 184, 0.25)">
            {Array.from({ length: 5 }, (_, index) => {
              const y = (chartHeight / 5) * index
              return <line key={index} x1={0} x2={chartWidth} y1={y} y2={y} />
            })}
          </g>
        )}
        {dataset.series.map((serie, serieIndex) => (
          <g key={serie.name}>
            {serie.values.map((value, valueIndex) => {
              const x = valueIndex * barGroupWidth * (dataset.series.length > 1 ? 1.2 : 1)
              const barWidth = dataset.series.length > 1 ? barGroupWidth / dataset.series.length : barGroupWidth * 0.6
              const offset = dataset.series.length > 1 ? serieIndex * barWidth : barGroupWidth * 0.2
              const height = (value / maxValue) * chartHeight
              const y = chartHeight - height
              const fill = serieIndex === 0 ? 'url(#bar-accent)' : 'url(#bar-secondary)'

              return (
                <rect
                  key={`${serie.name}-${valueIndex}`}
                  x={x + offset}
                  y={y}
                  width={barWidth - 8}
                  height={height}
                  rx={12}
                  fill={fill}
                />
              )
            })}
          </g>
        ))}
        <g transform={`translate(0, ${chartHeight})`}>
          {dataset.labels.map((label, index) => (
            <text key={label} x={index * barGroupWidth * (dataset.series.length > 1 ? 1.2 : 1) + 10} y={30} fill="rgba(148,163,184,0.9)" fontSize={12}>
              {label}
            </text>
          ))}
        </g>
      </g>
    </svg>
  )
}

/**
 * Line chart component with responsive SVG rendering
 * 
 * @param {Object} props - Component props
 * @param {ParsedDataset} props.dataset - The chart data
 * @param {ChartConfig} props.config - Chart configuration
 * @returns {JSX.Element} The rendered line chart SVG
 */
function LineChartGraphic({ dataset, config }: { dataset: ParsedDataset; config: ChartConfig }): JSX.Element {
  const maxValue = Math.max(getMaxValue(dataset) * 1.1, 1)
  const chartHeight = 320
  const chartWidth = Math.max(dataset.labels.length - 1, 1) * 120

  return (
    <svg viewBox={`0 0 ${chartWidth + 120} ${chartHeight + 120}`} className="h-full w-full" style={{ fontFamily: 'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif' }}>
      {/* Explicit fontFamily so the labels rasterize identically when the
          SVG is exported to PNG via an <img> (where the page/web font does
          not apply). */}
      <g transform="translate(60,40)">
        {config.showGrid && (
          <g stroke="rgba(148, 163, 184, 0.25)">
            {Array.from({ length: 5 }, (_, index) => {
              const y = (chartHeight / 5) * index
              return <line key={index} x1={0} x2={chartWidth} y1={y} y2={y} />
            })}
          </g>
        )}
        {dataset.series.map((serie, serieIndex) => {
          const stroke = serieIndex === 0 ? config.accent : config.secondary
          const path = serie.values
            .map((value, index) => {
              const x = (chartWidth / Math.max(dataset.labels.length - 1, 1)) * index
              const y = chartHeight - (value / maxValue) * chartHeight
              return `${index === 0 ? 'M' : 'L'} ${x} ${y}`
            })
            .join(' ')

          return (
            <g key={serie.name}>
              <path d={path} fill="none" stroke={stroke} strokeWidth={4} strokeLinecap="round" />
              {serie.values.map((value, index) => {
                const x = (chartWidth / Math.max(dataset.labels.length - 1, 1)) * index
                const y = chartHeight - (value / maxValue) * chartHeight
                return <circle key={`${serie.name}-${index}`} cx={x} cy={y} r={6} fill={stroke} stroke="#0f172a" strokeWidth={2} />
              })}
            </g>
          )
        })}
        <g transform={`translate(0, ${chartHeight})`}>
          {dataset.labels.map((label, index) => (
            <text key={label} x={(chartWidth / Math.max(dataset.labels.length - 1, 1)) * index} y={28} fill="rgba(148,163,184,0.9)" fontSize={12} textAnchor="middle">
              {label}
            </text>
          ))}
        </g>
      </g>
    </svg>
  )
}

/**
 * Main chart mockup generator component with responsive design and security features
 * Provides a complete interface for creating and customizing chart mockups
 *
 * @returns {JSX.Element} The complete chart generator interface
 */
export function ChartMockupGenerator(): JSX.Element {
  const [config, setConfig] = useState<ChartConfig>(DEFAULT_CONFIG)
  // Lower-priority snapshot of config that drives the heavy chart preview (the
  // SVG render + legend in `chartRef`). The controls (the aside) keep reading
  // live `config`, so title/dataset/color keystrokes commit synchronously while
  // the chart re-renders at a lower priority. Export waits for the snapshot to
  // converge so the PNG/SVG matches the live controls.
  const { deferred: deferredConfig, isStale } = usePreviewDeferral(config)
  const previewStaleRef = useStaleRef(isStale)
  const chartRef = useRef<HTMLDivElement>(null)

  // Shared export lifecycle: a synchronous re-entrancy guard and the busy flag.
  // `warm: false` — ChartMockup serializes its SVG natively / rasterizes via
  // `Image`+canvas, so it doesn't use html2canvas and shouldn't preload it.
  // `exportingFormat` tracks WHICH format is exporting so only the clicked
  // button shows the spinner while both are disabled.
  const { isExporting, runExport } = useExport({ warm: false })
  const [exportingFormat, setExportingFormat] = useState<'png' | 'svg' | null>(null)

  // Memoized parsed dataset to avoid unnecessary recalculations. Keyed to the
  // deferred snapshot since it feeds only the preview chart.
  const parsedDataset = useMemo(() => parseDataset(deferredConfig.dataset), [deferredConfig.dataset])

  // Memoized legend colors for performance. Feeds only the preview legend, so
  // it tracks the deferred snapshot.
  const legendSwatches = useMemo(() => [deferredConfig.accent, deferredConfig.secondary], [deferredConfig.accent, deferredConfig.secondary])

  /**
   * Randomizes the color palette with a curated set of accessible colors
   */
  const randomizePalette = useCallback(() => {
    const palette = ['#6366f1', '#22d3ee', '#f43f5e', '#f59e0b', '#10b981', '#a855f7', '#ec4899', '#8b5cf6']
    const shuffled = [...palette].sort(() => 0.5 - Math.random())
    const first = shuffled[0]
    const second = shuffled[1]
    
    // Ensure we have valid colors before updating state
    if (first && second) {
      setConfig(prev => ({ ...prev, accent: first, secondary: second }))
    }
  }, [])

  /**
   * Downloads the chart as an SVG file with proper error handling
   */
  const downloadSVG = useCallback((): Promise<void> => runExport(async beginCapture => {
    if (!chartRef.current) return

    // The preview renders from `deferredConfig`; wait for that snapshot to
    // converge so the exported SVG matches the live controls. Fail loudly if it
    // can't settle within the ceiling rather than export a stale frame.
    const stabilized = await waitForStable(() => !previewStaleRef.current)
    if (!stabilized) {
      toast.error('Chart is still updating — pause a moment, then export again.')
      return
    }
    // Freeze the converged title for the (synchronous here, but kept symmetric
    // with the PNG path) export so it can't drift mid-export.
    const frozenTitle = config.title

    setExportingFormat('svg')
    beginCapture()
    try {
      const svgElement = chartRef.current.querySelector('svg')
      if (!svgElement) return

      // Clone the SVG to avoid modifying the original
      const svgClone = svgElement.cloneNode(true) as SVGElement
      const svgData = new XMLSerializer().serializeToString(svgClone)
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' })
      const svgUrl = URL.createObjectURL(svgBlob)

      const downloadLink = document.createElement('a')
      downloadLink.href = svgUrl
      downloadLink.download = `chart-${frozenTitle.toLowerCase().replace(/\s+/g, '-')}.svg`
      document.body.appendChild(downloadLink)
      downloadLink.click()
      document.body.removeChild(downloadLink)
      URL.revokeObjectURL(svgUrl)

      // Track SVG download - zero cost
      sendResourceEvent('complete', {
        resourceType: 'tool',
        resourceSlug: 'chart-mockup-generator',
        params: { tool_type: 'generator', export_format: 'svg' }
      })
    } catch (error) {
      logClientError(error, { component: 'ChartMockupGenerator', context: 'Failed to download SVG' })
      sendResourceEvent('complete', {
        resourceType: 'tool',
        resourceSlug: 'chart-mockup-generator',
        params: { tool_type: 'generator', outcome: 'error', error_type: 'export_failed' },
      })
    } finally {
      setExportingFormat(null)
    }
  }), [runExport, config.title, previewStaleRef])

  /**
   * Rasterizes the chart SVG to a PNG at 2× the SVG's intrinsic size,
   * painting the configured `config.background` behind it first, then
   * triggers a download. Handles image onload/onerror so a failed
   * rasterization doesn't leak the object URL.
   */
  const downloadPNG = useCallback((): Promise<void> => runExport(async beginCapture => {
    if (!chartRef.current) return

    // The preview renders from `deferredConfig`; wait for that snapshot to
    // converge so the exported PNG matches the live controls. Fail loudly if it
    // can't settle within the ceiling rather than export a stale frame.
    const stabilized = await waitForStable(() => !previewStaleRef.current)
    if (!stabilized) {
      toast.error('Chart is still updating — pause a moment, then export again.')
      return
    }

    // Freeze the converged title + background and use them through the async
    // `Image.onload` rasterization below, so a config edit made WHILE the export
    // is in flight can't paint a new background under the already-serialized SVG
    // snapshot (or rename the file). Snapshotting after the wait guarantees these
    // match the SVG we serialize on the next line.
    const frozenTitle = config.title
    const frozenBackground = config.background

    const svgElement = chartRef.current.querySelector('svg')
    if (!svgElement) return

    // Consistent failure analytics — mirrors the `export_failed` event the
    // shared `captureAndDownload` helper emits for the html2canvas-based
    // generators, so a failed chart PNG export is never silent.
    const trackFailure = (): void => {
      sendResourceEvent('complete', {
        resourceType: 'tool',
        resourceSlug: 'chart-mockup-generator',
        params: { tool_type: 'generator', outcome: 'error', error_type: 'export_failed' },
      })
    }

    setExportingFormat('png')
    beginCapture()
    try {
      const rect = svgElement.getBoundingClientRect()
      const intrinsicWidth = svgElement.viewBox?.baseVal?.width || rect.width || 800
      const intrinsicHeight = svgElement.viewBox?.baseVal?.height || rect.height || 450
      const scale = 2
      const canvasWidth = Math.round(intrinsicWidth * scale)
      const canvasHeight = Math.round(intrinsicHeight * scale)

      const svgData = new XMLSerializer().serializeToString(svgElement)
      const svgUrl = `data:image/svg+xml;base64,${window.btoa(unescape(encodeURIComponent(svgData)))}`

      // Await the async `Image` rasterization so the busy state spans it and the
      // promise (hence `isExporting`) only settles once the PNG is written.
      // EVERY branch resolves so the button can never get stuck disabled.
      await new Promise<void>(resolve => {
        const image = new Image()
        image.onload = (): void => {
          try {
            const canvas = document.createElement('canvas')
            canvas.width = canvasWidth
            canvas.height = canvasHeight
            const ctx = canvas.getContext('2d')
            if (!ctx) {
              clientLogger.error('PNG export failed: no 2D context', { component: 'ChartMockupGenerator' })
              trackFailure()
              resolve()
              return
            }

            // Paint the configured background first so the PNG matches the
            // on-screen preview. `config.background` is a CSS value: a solid
            // color, or a `linear-gradient(...)` which we reproduce on the 2D
            // context (see `paintCanvasBackground`) so gradient backgrounds
            // export faithfully rather than as a flat fallback.
            paintCanvasBackground(ctx, frozenBackground, canvasWidth, canvasHeight)
            ctx.drawImage(image, 0, 0, canvasWidth, canvasHeight)

            canvas.toBlob(
              blob => {
                if (!blob) {
                  clientLogger.error('PNG export failed: empty blob', { component: 'ChartMockupGenerator' })
                  trackFailure()
                  resolve()
                  return
                }
                const url = URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.download = `chart-${frozenTitle.toLowerCase().replace(/\s+/g, '-')}.png`
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                URL.revokeObjectURL(url)

                sendResourceEvent('complete', {
                  resourceType: 'tool',
                  resourceSlug: 'chart-mockup-generator',
                  params: { tool_type: 'generator', export_format: 'png' }
                })
                resolve()
              },
              'image/png',
            )
          } catch (error) {
            logClientError(error, { component: 'ChartMockupGenerator', context: 'Failed to download PNG' })
            trackFailure()
            resolve()
          }
        }
        image.onerror = (): void => {
          clientLogger.error('PNG export failed: SVG could not be rasterized', { component: 'ChartMockupGenerator' })
          trackFailure()
          resolve()
        }
        image.src = svgUrl
      })
    } catch (error) {
      logClientError(error, { component: 'ChartMockupGenerator', context: 'Failed to download PNG' })
      trackFailure()
    } finally {
      setExportingFormat(null)
    }
  }), [runExport, config.title, config.background, previewStaleRef])

  /**
   * Updates config with sanitized values to prevent security issues
   */
  const updateConfig = useCallback((updates: Partial<ChartConfig>) => {
    setConfig(prev => ({
      ...prev,
      ...Object.fromEntries(
        Object.entries(updates).map(([key, value]) => [
          key,
          key === 'background' && typeof value === 'string' ? sanitizeBackground(value) :
          typeof value === 'string' ? sanitizeInput(value) : value
        ])
      )
    }))
  }, [])

  // Determine chart component based on type. The chart is the heavy preview, so
  // it renders from the deferred snapshot.
  // Memoize the chart ELEMENT so the per-keystroke urgent re-render (where
  // `deferredConfig` is still the previous, referentially-stable value) reuses
  // the same element object and React BAILS OUT of re-rendering the SVG chart.
  // Without this, `useDeferredValue` is a net loss: the heavy chart re-renders
  // on the urgent pass (old data) AND again on the deferred pass — double work
  // that *raised* INP in benchmarking (152ms → 256ms median). With the bail-out
  // the chart re-renders only once, on the low-priority deferred pass. Mirrors
  // the `useMemo(() => <Preview/>)` boundary in Browser/Email/Review.
  const chartGraphic = useMemo(
    () => deferredConfig.chartType === 'bar'
      ? <BarChart dataset={parsedDataset} config={deferredConfig} />
      : <LineChartGraphic dataset={parsedDataset} config={deferredConfig} />,
    [deferredConfig, parsedDataset],
  )

  return (
    <div className="flex w-full flex-col gap-6 lg:flex-row lg:gap-10">
      {/* Controls Panel */}
      <aside className="w-full space-y-6 rounded-3xl border border-slate-200 bg-white/80 p-6 shadow-xl backdrop-blur dark:border-slate-800 dark:bg-slate-900/70 lg:w-[420px]">
        
        {/* Chart Type Selection */}
        <section aria-labelledby="chart-type" className="space-y-3">
          <h2 id="chart-type" className="text-sm">
            <span className="font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Chart style</span>
          </h2>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <button
              type="button"
              onClick={() => updateConfig({ chartType: 'bar' })}
              className={clsx(
                'flex items-center justify-center gap-2 rounded-xl border px-4 py-3 font-medium transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900',
                config.chartType === 'bar'
                  ? 'border-transparent bg-slate-900 text-white shadow-lg dark:bg-indigo-500'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300'
              )}
            >
              <BarChart3 className="h-4 w-4" />
              Bar
            </button>
            <button
              type="button"
              onClick={() => updateConfig({ chartType: 'line' })}
              className={clsx(
                'flex items-center justify-center gap-2 rounded-xl border px-4 py-3 font-medium transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900',
                config.chartType === 'line'
                  ? 'border-transparent bg-slate-900 text-white shadow-lg dark:bg-indigo-500'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300'
              )}
            >
              <LineChart className="h-4 w-4" />
              Line
            </button>
          </div>
        </section>

        <section aria-labelledby="chart-content" className="space-y-3">
          <h2 id="chart-content" className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Narrative
          </h2>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-slate-700 dark:text-slate-200">Title</span>
            <input
              type="text"
              value={config.title}
              onChange={event => updateConfig({ title: event.target.value })}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-slate-700 dark:text-slate-200">Subtitle</span>
            <input
              type="text"
              value={config.subtitle}
              onChange={event => updateConfig({ subtitle: event.target.value })}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
            />
          </label>
        </section>

        <section aria-labelledby="dataset" className="space-y-3">
          <h2 id="dataset" className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Dataset
          </h2>
          <textarea
            value={config.dataset}
            onChange={event => updateConfig({ dataset: event.target.value })}
            className="min-h-[160px] w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-mono leading-6 text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          />
          <p className="text-xs text-slate-500 dark:text-slate-300">
            CSV style input with headings. Each additional column becomes a data series.
          </p>
        </section>

        <section aria-labelledby="appearance" className="space-y-3">
          <h2 id="appearance" className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Appearance
          </h2>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <label className="flex flex-col gap-1">
              <span className="font-medium text-slate-700 dark:text-slate-200">Accent</span>
              <input
                type="color"
                value={config.accent}
                onChange={event => updateConfig({ accent: event.target.value })}
                className="h-10 w-full rounded-md border border-slate-300 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="font-medium text-slate-700 dark:text-slate-200">Secondary</span>
              <input
                type="color"
                value={config.secondary}
                onChange={event => updateConfig({ secondary: event.target.value })}
                className="h-10 w-full rounded-md border border-slate-300 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900"
              />
            </label>
          </div>
          <button
            type="button"
            onClick={randomizePalette}
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:bg-indigo-500 dark:hover:bg-indigo-400"
          >
            <Palette className="h-4 w-4" />
            Shuffle palette
          </button>
        </section>

        <section aria-labelledby="background" className="space-y-3">
          <h2 id="background" className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Background
          </h2>
          <div className="grid grid-cols-2 gap-2">
            {BACKGROUND_PRESETS.map(preset => (
              <button
                key={preset.name}
                type="button"
                onClick={() => updateConfig({ background: preset.value })}
                className={clsx(
                  'flex h-12 items-center justify-center rounded-lg border text-xs font-medium transition',
                  config.background === preset.value
                    ? 'border-slate-900 ring-2 ring-slate-900 ring-offset-2 dark:border-indigo-500 dark:ring-indigo-500'
                    : 'border-slate-200 hover:border-slate-300 dark:border-slate-700 dark:hover:border-slate-600',
                  preset.preview.startsWith('bg-') ? preset.preview : `bg-gradient-to-br ${preset.preview}`
                )}
                title={preset.name}
              >
                <span className={clsx(
                  'px-2 py-1 rounded text-xs font-medium',
                  preset.value === '#ffffff' ? 'text-slate-900 bg-white/80' : 'text-white bg-black/20'
                )}>
                  {preset.name}
                </span>
              </button>
            ))}
          </div>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-slate-700 dark:text-slate-200">Custom CSS</span>
            <input
              type="text"
              value={config.background}
              onChange={event => updateConfig({ background: event.target.value })}
              placeholder="e.g., #ffffff or linear-gradient(...)"
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-mono text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
            />
          </label>
        </section>

        <section aria-labelledby="options" className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <label className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
              <input
                type="checkbox"
                checked={config.showGrid}
                onChange={event => updateConfig({ showGrid: event.target.checked })}
                className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900 dark:border-slate-600 dark:text-indigo-500 dark:focus:ring-indigo-500"
              />
              Grid lines
            </label>
            <label className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
              <input
                type="checkbox"
                checked={config.showLegend}
                onChange={event => updateConfig({ showLegend: event.target.checked })}
                className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900 dark:border-slate-600 dark:text-indigo-500 dark:focus:ring-indigo-500"
              />
              Legend
            </label>
          </div>
        </section>

        <section aria-labelledby="export" className="space-y-3">
          <h2 id="export" className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Export
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-300">
            Download a high-resolution PNG for decks and docs, or a scalable SVG for design tools. Both render at 2× clarity.
          </p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <ExportButton
              isExporting={exportingFormat === 'png'}
              disabled={isExporting}
              onExport={() => void downloadPNG()}
              icon={<Download className="h-4 w-4" />}
              className="w-full rounded-lg bg-slate-900 px-4 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400"
            />
            <ExportButton
              isExporting={exportingFormat === 'svg'}
              disabled={isExporting}
              onExport={() => void downloadSVG()}
              icon={<Download className="h-4 w-4" />}
              label="Download SVG"
              className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            />
          </div>
        </section>
      </aside>

      {/* Chart Preview - Back to original clean design. Renders from the
          deferred snapshot so the heavy chart re-renders at a lower priority. */}
      <div className="flex flex-1 items-center justify-center rounded-3xl border border-white/10 p-4 sm:p-6 lg:p-10" style={{ background: deferredConfig.background }}>
        <div className="w-full max-w-4xl space-y-6 rounded-[32px] border border-white/10 bg-black/50 p-6 sm:p-8 shadow-[0_40px_120px_-35px_rgba(15,23,42,0.8)] backdrop-blur">
          <header className="space-y-1">
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400">AdSights Insights</p>
            <h2 className="text-xl sm:text-2xl font-semibold text-white">{deferredConfig.title}</h2>
            <p className="text-sm text-slate-300">{deferredConfig.subtitle}</p>
          </header>
          <div ref={chartRef} className="h-[280px] sm:h-[360px] w-full overflow-hidden rounded-3xl border border-white/10 bg-slate-950/70 p-4 sm:p-6">
            {chartGraphic}
          </div>
          {deferredConfig.showLegend && parsedDataset.series.length > 0 && (
            <div className="flex flex-wrap items-center gap-4 text-xs font-semibold uppercase tracking-wide text-slate-300">
              {parsedDataset.series.map((serie, index) => (
                <span key={serie.name} className="inline-flex items-center gap-2">
                  <span
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ background: legendSwatches[index] ?? deferredConfig.accent }}
                  />
                  {serie.name}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
